import { Loader2, UserCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export interface PendingMembershipApproval {
  groupId: string;
  groupName: string;
  memberDisplayName: string;
  memberInitial: string;
  approvalTokens: string[];
}

interface HomeMembershipApprovalModalProps {
  approval: PendingMembershipApproval;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isApproving: boolean;
  isRejecting: boolean;
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
}

const HomeMembershipApprovalModal = ({
  approval,
  open,
  onOpenChange,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
}: HomeMembershipApprovalModalProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-[95%] p-0 sm:max-w-md">
      <DialogHeader className="p-4 pb-0 sm:p-6">
        <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary shadow-primary sm:h-10 sm:w-10">
            <UserCheck className="h-4 w-4 text-primary-foreground sm:h-5 sm:w-5" />
          </div>
          Meal Logged for You
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-3 p-4 sm:px-6 sm:pt-3">
        <div className="flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 dark:border-violet-800 dark:bg-violet-950/20">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-sm font-bold text-primary-foreground shadow-primary">
            {approval.groupName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">
              {approval.groupName}
            </p>
            <p className="text-xs text-muted-foreground">
              Member:{" "}
              <span className="font-medium text-foreground">
                {approval.memberDisplayName}
              </span>
            </p>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">
          A meal has been logged for{" "}
          <span className="font-semibold text-foreground">
            {approval.memberDisplayName}
          </span>{" "}
          in the{" "}
          <span className="font-semibold text-foreground">{approval.groupName}</span>{" "}
          group. If you have eaten it, tap{" "}
          <span className="font-semibold text-green-600">Approve</span>. If you have
          not eaten it, tap{" "}
          <span className="font-semibold text-red-500">Reject</span>.
        </p>
      </div>

      <div className="flex gap-2 p-4 sm:px-6 sm:pb-6">
        <button
          type="button"
          onClick={() => void onReject()}
          disabled={isApproving || isRejecting}
          className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md border border-red-300 bg-red-50 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:bg-red-950/20 dark:text-red-400 sm:h-11"
        >
          {isRejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Reject
        </button>
        <button
          type="button"
          onClick={() => void onApprove()}
          disabled={isApproving || isRejecting}
          className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md bg-gradient-primary text-sm font-semibold text-primary-foreground shadow-primary transition-opacity hover:opacity-90 disabled:opacity-50 sm:h-11"
        >
          {isApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Approve
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

export default HomeMembershipApprovalModal;
